import React, { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { MdSettings } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, closeModalWithUpdate } from "../../../features/modal/ModalSlice";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography, Divider } from "@mui/material";
import CustomButton from "../../Button/CustomButton";
import apiClient from "../../../services/apiClient";
import toast from "react-hot-toast";

const ConfigureNumberAllocation = () => {
  const [formData, setFormData] = useState({ extension: "", user: "" });
  const [extensions, setExtensions] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);

  const modal = useSelector((state) => state?.modal?.modal);
  const userInfo = useSelector((state) => state?.userInfo?.user);

  const dispatch = useDispatch();

  useEffect(() => {
    fetchExtensions();
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      if (!modal?.data) {
        const response = await apiClient.get(`/UsersByCompany/${userInfo?.company_id}`);
        setTeam(response?.data?.users || []);
      } else setTeam([modal?.data]);
    } catch (error) {
      console.error("Error fetching users by company:", error);
    }
  };

  const fetchExtensions = async () => {
    try {
      const response = await apiClient.get(
        `/assignedPhoneDetails?owner_id=${userInfo?.id}&company_id=${userInfo?.company_id}&assigned_phone_status=not-assigned&page=1&page_size=100`
      );
      setExtensions(response?.data || []);
    } catch (error) {
      console.error("Error fetching extensions:", error);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClosePopup = () => {
    dispatch(closeModal());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.extension || !formData.user) {
      toast.error("Please select both extension and user");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        user_id: formData?.user?._id,
        assigned_phone_status: "assigned",
      };
      await apiClient.put(`/assignedPhoneDetails-update/${formData?.extension?._id}`, payload);

      toast.success("Extension Assigned Successfully!");
      dispatch(
        closeModalWithUpdate({
          isUpdated: {
            isUpdatedIn: "TEAMS",
            data: null,
          },
        })
      );
    } catch (error) {
      console.error("Error assigning extension:", error);
      toast.error("Oops! An error occurred. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rightModal taskModal">
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <MdSettings style={{ color: "white", fontSize: 24 }} />
          <Typography variant="h5" sx={{ color: "white", fontWeight: 600, m: 0 }}>
            Configure Number
          </Typography>
        </Box>
        <button
          className="btnClose"
          onClick={handleClosePopup}
          style={{
            background: "rgba(255, 255, 255, 0.2)",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
        >
          <IoCloseOutline style={{ color: "white", fontSize: 20 }} />
        </button>
      </Box>

      <Divider />

      {/* Body */}
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          Assign an available extension to a team member. This will allow them to make and receive calls using the selected extension.
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Extension Dropdown */}
            <FormControl fullWidth>
              <InputLabel id="extension-label">Extension</InputLabel>
              <Select
                labelId="extension-label"
                label="Extension"
                id="extension"
                value={formData.extension}
                onChange={(e) => handleFieldChange("extension", e.target.value)}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select an extension</em>
                </MenuItem>
                {extensions?.length > 0 ? (
                  extensions.map((ext, index) => (
                    <MenuItem key={index} value={ext}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ext?.data?.username}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          {ext?.data?.effective_caller_id_number}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    <em>No extensions available</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* User Dropdown */}
            <FormControl fullWidth>
              <InputLabel id="user-label">User</InputLabel>
              <Select
                labelId="user-label"
                label="User"
                id="user"
                value={formData.user}
                onChange={(e) => handleFieldChange("user", e.target.value)}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select a user</em>
                </MenuItem>
                {team?.length > 0 ? (
                  team.map((user, index) => (
                    <MenuItem key={index} value={user}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "#E5E1FA",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#667eea",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          {user?.name?.substring(0, 1).toUpperCase()}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user?.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6B7280" }}>
                            {user?.email_id}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    <em>No users available</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Info Box */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "#F0F9FF",
                borderRadius: "8px",
                border: "1px solid #BAE6FD",
                display: "flex",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: "#0EA5E9",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              >
                i
              </Box>
              <Typography variant="body2" sx={{ color: "#0369A1", fontSize: "0.875rem", lineHeight: 1.6 }}>
                Once assigned, the user will be able to use this extension for making and receiving calls. You can unassign it later from the team members page.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Footer */}
      <Box className="d-flex rightModalFooterBtn" sx={{ borderTop: "1px solid #E5E7EB" }}>
        <CustomButton
          type="button"
          variant="contained"
          onClick={handleClosePopup}
          disabled={loading}
          sx={{
            minWidth: "40%",
            borderRadius: "0px",
            backgroundColor: "#FFFFFF",
            color: "#333",
            padding: "14px 16px",
            fontWeight: 500,
            fontSize: "0.938rem",
            boxShadow: "none",
            borderRight: "1px solid #E5E7EB",
            "&:hover": {
              backgroundColor: "#F9FAFB",
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#F3F4F6",
              color: "#9CA3AF",
            },
          }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          loading={loading}
          disabled={!formData.extension || !formData.user}
          sx={{
            minWidth: "60%",
            borderRadius: "0px",
            backgroundColor: "#667eea",
            color: "#FFFFFF",
            padding: "14px 16px",
            fontWeight: 600,
            fontSize: "0.938rem",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#5568d3",
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          Assign Extension
        </CustomButton>
      </Box>
    </div>
  );
};

export default ConfigureNumberAllocation;
